import { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { Application } from '@frontend/functionality/types';
import { useAccountRepository } from '@frontend/repository';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { ExtendedPudoTransactionListResponse, TransactionStateName } from '@frontend/transaction/types';
import { useCallback } from 'react';

import { useAppSelector } from '../../redux/store';
import { usePudoListSort } from './use-pudo-list-sort';

export type PudoAwaitingBadgeType = 'awaiting-drop-off' | 'awaiting-pick-up';

export interface UsePudoAwaitingListArgs {
    state: TransactionStateName;
    sortField: 'creation_timestamp' | 'dropped_off_timestamp';
    badgeType: PudoAwaitingBadgeType;
}

export interface PudoAwaitingListViewProps {
    badgeType: PudoAwaitingBadgeType;
    isNewestFirst: boolean;
    toggleSortDirection: () => void;
    fetch: (queryParams?: ApiQueryParams<DefaultQueryParams>) => Promise<ExtendedPudoTransactionListResponse>;
}

export const usePudoAwaitingList = (args: UsePudoAwaitingListArgs): PudoAwaitingListViewProps => {
    const { state, sortField, badgeType } = args;
    const { currentAccount } = useAccountRepository();
    const spotId = useAppSelector((store) => store.navigation.spot?.id);
    const { sortDirection, isNewestFirst, toggleSortDirection } = usePudoListSort();

    const fetch = useCallback(
        async (queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<ExtendedPudoTransactionListResponse> => {
            if (!currentAccount || !spotId) {
                return { results: [], count: 0 };
            }
            return TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                ...queryParams,
                account_id: currentAccount,
                spot_id: spotId,
                state,
                application: Application.PUDO,
                sort_field: sortField,
                sort_direction: sortDirection
            });
        },
        [currentAccount, sortDirection, sortField, spotId, state]
    );

    return {
        badgeType,
        isNewestFirst,
        toggleSortDirection,
        fetch
    };
};
